import type {
  BatteryInfo,
  BloodGlucoseData,
  ConnectionStatus,
  DaySummaryData,
  DeviceFunctions,
  DeviceVersion,
  HalfHourData,
  OriginData,
  SleepData,
  SocialMsgData,
  SportStepData,
  Spo2OriginData,
  StressData,
} from './types.js';
import type {
  MeasurementState,
  NormalizedBloodGlucoseMeasurementResult,
  NormalizedStressMeasurementResult,
} from './unified-api.js';

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
};

const toInt = (value: unknown, fallback = 0): number => Math.trunc(toFiniteNumber(value, fallback));

const toBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    if (value === 'true' || value === '1') return true;
    if (value === 'false' || value === '0') return false;
  }
  return fallback;
};

const toOptionalPositiveInt = (value: unknown): number | undefined => {
  const normalized = toInt(value);
  return normalized > 0 ? normalized : undefined;
};

const toOptionalPositiveNumber = (value: unknown): number | undefined => {
  const normalized = toFiniteNumber(value);
  return normalized > 0 ? normalized : undefined;
};

const sortByTime = <T extends { time: string }>(items: T[]): T[] => [...items].sort((a, b) => a.time.localeCompare(b.time));

export const normalizeSleepData = (data: SleepData[] | SleepData, fallbackDate: string): SleepData => {
  if (!Array.isArray(data)) {
    return {
      date: data.date || fallbackDate,
      items: data.items ?? [],
      summary: {
        totalDeepSleepMinutes: toInt(data.summary?.totalDeepSleepMinutes),
        totalLightSleepMinutes: toInt(data.summary?.totalLightSleepMinutes),
        totalSleepMinutes: toInt(data.summary?.totalSleepMinutes),
        averageSleepQuality: toInt(data.summary?.averageSleepQuality),
        totalWakeUpCount: toInt(data.summary?.totalWakeUpCount),
      },
    };
  }

  if (data.length === 0) {
    return {
      date: fallbackDate,
      items: [],
      summary: {
        totalDeepSleepMinutes: 0,
        totalLightSleepMinutes: 0,
        totalSleepMinutes: 0,
        averageSleepQuality: 0,
        totalWakeUpCount: 0,
      },
    };
  }

  if (data.length === 1) {
    return normalizeSleepData(data[0], fallbackDate);
  }

  const mergedItems = data.flatMap((entry) => entry.items ?? []);

  return {
    date: fallbackDate,
    items: mergedItems,
    summary: {
      totalDeepSleepMinutes: data.reduce((sum, entry) => sum + toInt(entry.summary?.totalDeepSleepMinutes), 0),
      totalLightSleepMinutes: data.reduce((sum, entry) => sum + toInt(entry.summary?.totalLightSleepMinutes), 0),
      totalSleepMinutes: data.reduce((sum, entry) => sum + toInt(entry.summary?.totalSleepMinutes), 0),
      averageSleepQuality: Math.round(
        data.reduce((sum, entry) => sum + toInt(entry.summary?.averageSleepQuality), 0) / data.length
      ),
      totalWakeUpCount: data.reduce((sum, entry) => sum + toInt(entry.summary?.totalWakeUpCount), 0),
    },
  };
};

export const normalizeSportData = (data: SportStepData, fallbackDate: string): SportStepData => ({
  date: data.date || fallbackDate,
  stepCount: toInt(data.stepCount),
  distance: toFiniteNumber(data.distance),
  calories: toFiniteNumber(data.calories),
});

export const normalizeBatteryInfo = (data: BatteryInfo): BatteryInfo => {
  const level = toInt(data.level);
  const percent = toInt(data.percent || data.level);
  const state = toInt(data.state);
  const chargeState = data.chargeState ?? (
    state === 1 ? 'charging' : state === 2 ? 'full' : percent <= 10 ? 'lowPressure' : 'normal'
  );

  return {
    ...data,
    level,
    percent,
    powerModel: toInt(data.powerModel),
    state,
    bat: toInt(data.bat || data.level),
    isPercent: toBoolean(data.isPercent, true),
    isLowBattery: toBoolean(data.isLowBattery, percent <= 10),
    chargeState,
  };
};

export const normalizeDeviceFunctions = (data: DeviceFunctions): DeviceFunctions => ({
  package1: data.package1 ?? {},
  package2: data.package2 ?? {},
  package3: data.package3 ?? {},
  package4: data.package4 ?? {},
  package5: data.package5,
});

export const normalizeDeviceVersion = (data: DeviceVersion): DeviceVersion => ({
  hardwareVersion: data.hardwareVersion ?? '',
  firmwareVersion: data.firmwareVersion ?? '',
  softwareVersion: data.softwareVersion ?? '',
  deviceNumber: data.deviceNumber ?? '',
  newVersion: data.newVersion ?? '',
  description: data.description ?? '',
});

export const normalizeSocialMsgData = (data: SocialMsgData): SocialMsgData => ({ ...data });

export const normalizeOriginDataList = (data: OriginData[]): OriginData[] =>
  sortByTime(
    data.map((item) => ({
      ...item,
      time: item.time ?? '',
      heartValue: toInt(item.heartValue),
      stepValue: toInt(item.stepValue),
      calValue: toFiniteNumber(item.calValue),
      disValue: toFiniteNumber(item.disValue),
      sportValue: toInt(item.sportValue),
      systolic: toInt(item.systolic),
      diastolic: toInt(item.diastolic),
      spo2Value: toInt(item.spo2Value),
      tempValue: toFiniteNumber(item.tempValue),
      stressValue: toInt(item.stressValue),
      met: toFiniteNumber(item.met),
      oxygens: item.oxygens,
      ppgs: item.ppgs,
      ecgs: item.ecgs,
      resRates: item.resRates,
      sleepStates: item.sleepStates,
      apneaResults: item.apneaResults,
      hypoxiaTimes: item.hypoxiaTimes,
      cardiacLoads: item.cardiacLoads,
      bloodGlucose: item.bloodGlucose,
    }))
  );

export const normalizeHalfHourDataList = (data: HalfHourData[]): HalfHourData[] =>
  sortByTime(
    data.map((item) => ({
      ...item,
      time: item.time ?? '',
      heartValue: toOptionalPositiveInt(item.heartValue),
      sportValue: toOptionalPositiveInt(item.sportValue),
      stepValue: item.stepValue == null ? undefined : toInt(item.stepValue),
      calValue: item.calValue == null ? undefined : toFiniteNumber(item.calValue),
      disValue: item.disValue == null ? undefined : toFiniteNumber(item.disValue),
      diastolic: toOptionalPositiveInt(item.diastolic),
      systolic: toOptionalPositiveInt(item.systolic),
      spo2Value: toOptionalPositiveInt(item.spo2Value),
      tempValue: toOptionalPositiveNumber(item.tempValue),
      stressValue: toOptionalPositiveInt(item.stressValue),
      met: toOptionalPositiveNumber(item.met),
      bloodGlucose: toOptionalPositiveInt(item.bloodGlucose),
      glucose: toOptionalPositiveNumber(item.glucose),
      bloodGlucoseLevel: item.bloodGlucoseLevel,
      bloodGlucoses: item.bloodGlucoses,
    }))
  );

export const normalizeDaySummaryData = (data: DaySummaryData, fallbackDate: string): DaySummaryData => ({
  date: data.date || fallbackDate,
  allStep: toInt(data.allStep),
  sportList: (data.sportList ?? []).map((item) => ({
    time: item.time ?? '',
    step: toInt(item.step),
    cal: toFiniteNumber(item.cal),
    dis: toFiniteNumber(item.dis),
  })),
  rateList: (data.rateList ?? []).map((item) => ({
    time: item.time ?? '',
    rate: toInt(item.rate),
  })),
  bpList: (data.bpList ?? []).map((item) => ({
    time: item.time ?? '',
    high: toInt(item.high),
    low: toInt(item.low),
  })),
});

export const normalizeSpo2OriginDataList = (data: Spo2OriginData[]): Spo2OriginData[] =>
  sortByTime(
    data.map((item) => ({
      ...item,
      time: item.time ?? '',
      date: item.date ?? '',
      heartValue: toInt(item.heartValue),
      value: toInt(item.value),
      rate: toInt(item.rate),
      isHypoxia: toInt(item.isHypoxia),
      cardiacLoad: toInt(item.cardiacLoad),
      temp1: toInt(item.temp1),
      sportValue: toInt(item.sportValue),
      apneaResult: toInt(item.apneaResult),
      hypoxiaTime: toInt(item.hypoxiaTime),
      hypopnea: toInt(item.hypopnea),
      stepValue: toInt(item.stepValue),
      allPackNumber: toInt(item.allPackNumber),
      currentPackNumber: toInt(item.currentPackNumber),
    }))
  );

export const normalizeMeasurementState = (value: string): MeasurementState['state'] => {
  const normalized = value.trim();
  switch (normalized) {
    case 'idle':
    case 'start':
    case 'testing':
    case 'notWear':
    case 'deviceBusy':
    case 'over':
    case 'error':
      return normalized;
    default:
      return 'error';
  }
};

export const normalizeBloodGlucoseLevel = (value: string | number | undefined): NormalizedBloodGlucoseMeasurementResult['level'] => {
  if (value == null) {
    return 'unknown';
  }

  if (typeof value === 'number') {
    switch (value) {
      case 1:
        return 'low';
      case 2:
        return 'normal';
      case 3:
        return 'high';
      default:
        return 'unknown';
    }
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return 'unknown';
  }
  if (normalized.includes('low')) {
    return 'low';
  }
  if (normalized.includes('high')) {
    return 'high';
  }
  if (normalized.includes('normal') || normalized.includes('middle') || normalized.includes('mid')) {
    return 'normal';
  }
  if (normalized.includes('unknown')) {
    return 'unknown';
  }

  return value;
};

export const normalizeStressMeasurement = (data: StressData): NormalizedStressMeasurementResult => {
  const timestamp = data.timestamp != null ? toInt(data.timestamp) : Date.now();
  const progress = data.progress == null ? undefined : toInt(data.progress);
  const status = data.status?.trim();

  let state: NormalizedStressMeasurementResult['state'];
  let reason: NormalizedStressMeasurementResult['reason'] | undefined;

  switch (status) {
    case 'deviceBusy':
      state = 'deviceBusy';
      reason = 'deviceBusy';
      break;
    case 'notWear':
      state = 'notWear';
      reason = 'notWear';
      break;
    case 'unsupported':
    case 'noFunction':
      state = 'error';
      reason = 'unsupported';
      break;
    case 'lowPower':
      state = 'error';
      reason = 'lowPower';
      break;
    case 'complete':
      state = 'over';
      reason = 'complete';
      break;
    case 'over':
      state = 'over';
      break;
    case 'start':
      state = 'start';
      break;
    default:
      state = data.isEnd ? 'over' : progress != null && progress <= 0 ? 'start' : 'testing';
      break;
  }

  return {
    ...data,
    stress: toInt(data.stress),
    timestamp,
    progress,
    status,
    isEnd: data.isEnd ?? state === 'over',
    state,
    reason,
  };
};

export const normalizeBloodGlucoseMeasurement = (
  data: BloodGlucoseData
): NormalizedBloodGlucoseMeasurementResult => {
  const timestamp = data.timestamp == null ? Date.now() : toInt(data.timestamp);
  const progress = data.progress == null ? undefined : toInt(data.progress);
  const status = data.status?.trim();
  const rawState = (data.state ?? status ?? (data.isEnd ? 'over' : progress != null && progress <= 0 ? 'start' : 'testing')).trim();
  let normalizedState = normalizeMeasurementState(rawState);
  let reason: NormalizedBloodGlucoseMeasurementResult['reason'] | undefined;

  switch (rawState) {
    case 'unsupported':
      normalizedState = 'error';
      reason = 'unsupported';
      break;
    case 'lowPower':
      normalizedState = 'error';
      reason = 'lowPower';
      break;
    case 'deviceBusy':
      normalizedState = 'deviceBusy';
      reason = 'deviceBusy';
      break;
    case 'notWear':
      normalizedState = 'notWear';
      reason = 'notWear';
      break;
    case 'close':
    case 'complete':
      normalizedState = 'over';
      break;
    case 'open':
      normalizedState = progress != null && progress <= 0 ? 'start' : 'testing';
      break;
    default:
      break;
  }

  return {
    state: normalizedState,
    glucose: data.glucose == null ? undefined : toFiniteNumber(data.glucose),
    progress,
    level: normalizeBloodGlucoseLevel(data.level),
    isEnd: data.isEnd ?? (
      normalizedState === 'over' ||
      normalizedState === 'error' ||
      normalizedState === 'deviceBusy' ||
      normalizedState === 'notWear'
    ),
    error: data.error,
    status,
    timestamp,
    reason,
  };
};

export const isPasswordAccepted = (status: string): boolean => status === 'SUCCESS' || status === 'CHECK_SUCCESS';

export const normalizeConnectionStatus = (status: string): ConnectionStatus => {
  switch (status) {
    case 'connecting':
    case 'connected':
    case 'disconnecting':
    case 'disconnected':
    case 'ready':
    case 'error':
      return status;
    default:
      return 'error';
  }
};
